import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import { Autocomplete, ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  getScorecardWeekDetail,
  getTeacherScorecard
} from 'api/teacherScorecard'
import { centreOf } from 'app/pages/Teacher/Attendance/Attendance'

import ActionsTab from './ActionsTab'
import { kekaCentreFor } from './centreMatch'
import { C, PunctualitySection, fmtDate } from './Punctuality'
import {
  SECTIONS,
  addDays,
  averageScores,
  band,
  buckets,
  isLeader,
  isPreschool,
  monthDays,
  moments,
  MOMENTS_A_MONTH,
  observedBetween,
  prevMonth,
  scoresFor,
  scoresForDates,
  weekDays,
  weeklyWork
} from './scoring'
import useCalendarDaysOff from './useCalendarDaysOff'

// Centre Audit > Scorecard (2026-10-17). One row per Keka teacher: four
// sections, each out of 5, for the last 4 weeks or a month; a trend chart; click
// a teacher for the facts behind every score. Environment and Facilitation
// come from Centre Ops > Classroom Observations. The scoring rules are in
// scoring.js.

// Always 6 months of weeks (Satya, 22 Oct: the trend is fixed at six
// months, no 3 / 6 choice). The Teachers tab's months reach back as far.
const WEEKS = 26
// While older weeks are still being filled in on the server (the first fill
// after release), the page asks again this often and fills in by itself.
const FILL_RETRY_MS = 30000
const ALL = '__all__'
// Teachers tab period: the last 4 weeks, or one calendar month (Shruti, 19
// Oct: "month by month as well"), as far back as the 26 weeks the API serves.
export const LAST4 = 'last4'
export const CUSTOM = 'custom'
// Custom dates can reach back as far as the 26 weeks the API serves.
const MAX_BACK_DAYS = 26 * 7 - 7
const MONTHS_BACK = 6
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  title: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  info: { color: C.ink2, fontSize: 20, cursor: 'help' },
  infoTip: { maxWidth: 860, padding: '12px 14px' },
  bar: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  select: { minWidth: 190, background: '#fff' },
  search: { minWidth: 240, background: '#fff' },
  card: {
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    background: '#fff',
    marginBottom: theme.spacing(3)
  },
  cardHead: { padding: '14px 16px 6px' },
  h2: { fontWeight: 700, fontSize: 16 },
  sub: { color: C.ink2, fontSize: 12 },
  scroll: { overflowX: 'auto' },
  tabs: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${C.line}`,
    '& .MuiTab-root': { textTransform: 'none' }
  },
  tableHint: { color: C.ink2, fontSize: 12, marginBottom: 8 },
  // Sticky to the WINDOW: the table sits in no overflow box, so the header
  // row rises to the top of the page as it scrolls.
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    background: '#fff',
    border: `1px solid ${C.line}`,
    borderRadius: 10
  },
  // Light band header (option B, 17 Oct): a soft warm-grey strip, not dark.
  th: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: C.band,
    color: '#1f2430',
    fontWeight: 600,
    fontSize: 12.5,
    textAlign: 'center',
    // Column names wrap onto two lines so all ten columns fit the window
    // (Satya, 22 Oct: no side-scrolling after the two new columns).
    padding: '8px 6px',
    verticalAlign: 'middle',
    borderBottom: `1px solid ${C.line}`,
    '&:first-child': { borderTopLeftRadius: 10, textAlign: 'left' },
    '&:last-child': { borderTopRightRadius: 10 }
  },
  thWho: { width: '24%', minWidth: 190 },
  headSearch: {
    maxWidth: 300,
    background: '#fff',
    '& input': { fontSize: 12.5, padding: '6px 8px 6px 0' },
    '& input::placeholder': { color: '#1f2430', opacity: 0.75, fontWeight: 600 }
  },
  sortBtn: {
    border: 0,
    background: 'none',
    padding: '2px 4px',
    font: 'inherit',
    fontWeight: 600,
    color: 'inherit',
    cursor: 'pointer',
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    lineHeight: 1.25,
    '&:focus-visible': { outline: `2px solid ${C.navy}` }
  },
  arrow: { fontSize: 10, opacity: 0.35, whiteSpace: 'nowrap' },
  arrowOn: { opacity: 1, color: '#F25E35' },
  td: {
    borderBottom: `1px solid ${C.line}`,
    padding: '7px 6px',
    verticalAlign: 'middle',
    fontVariantNumeric: 'tabular-nums'
  },
  tdNum: { textAlign: 'center', fontWeight: 600, fontSize: 14 },
  row: {
    cursor: 'pointer',
    '&:hover': { background: '#FAF7F3' },
    '&:focus-visible': { outline: `2px solid ${C.navy}`, outlineOffset: -2 }
  },
  name: { fontWeight: 600, fontSize: 14 },
  pill: {
    display: 'inline-block',
    minWidth: 40,
    textAlign: 'center',
    borderRadius: 999,
    padding: '2px 9px',
    fontWeight: 700,
    fontSize: 13
  },
  good: { background: C.goodBg, color: C.good },
  ok: { background: C.okBg, color: C.ok },
  weak: { background: C.weakBg, color: C.weak },
  none: { color: C.ink2, fontWeight: 400 },
  up: { color: C.good, fontWeight: 600 },
  down: { color: C.weak, fontWeight: 600 },
  dot: { width: 9, height: 9, borderRadius: '50%', display: 'inline-block' },
  // Trend: one small card per section (23 Oct: six lines on one chart was
  // too tangled to read).
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 12,
    padding: '0 16px 16px'
  },
  miniCard: {
    border: `1px solid ${C.line}`,
    borderRadius: 8,
    padding: '10px 12px 4px'
  },
  miniHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 600
  },
  miniNow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 },
  empty: { padding: 30, textAlign: 'center', color: C.ink2 },
  loading: { padding: 60, textAlign: 'center' },
  // detail dialog
  subTabs: { display: 'flex', alignItems: 'center', gap: 8 },
  groupSwitch: {
    marginLeft: 16,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontSize: 13,
      padding: '4px 14px',
      color: '#555',
      borderColor: '#E0E0E0'
    },
    '& .MuiToggleButton-root.Mui-selected': {
      color: theme.palette.primary.main,
      background: '#FDF1EC',
      borderColor: theme.palette.primary.main,
      fontWeight: 600
    },
    '& .MuiToggleButton-root.Mui-selected:hover': { background: '#FBE5DB' }
  },
  weekRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  weekBox: {
    border: 0,
    borderRadius: 6,
    padding: '6px 10px',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer'
  },
  weekTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
    marginTop: 8,
    '& th': {
      textAlign: 'left',
      fontSize: 12,
      color: '#5F6470',
      padding: '6px 8px'
    },
    '& td': { padding: '6px 8px', borderTop: '1px solid #E6E8EC' }
  },
  dHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 8
  },
  monthStep: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 8
  },
  sec: {
    borderTop: `1px solid ${C.line}`,
    paddingTop: 14,
    marginTop: 14
  },
  secHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6
  },
  secTitle: {
    fontWeight: 700,
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  calc: { color: C.ink2, fontSize: 12.5, marginBottom: 8 },
  mini: { borderCollapse: 'collapse', fontSize: 13, width: '100%' },
  miniTh: {
    textAlign: 'left',
    color: C.ink2,
    fontWeight: 600,
    fontSize: 12,
    padding: '4px 8px',
    borderBottom: `1px solid ${C.line}`
  },
  miniTd: { padding: '5px 8px', borderBottom: `1px solid ${C.line}` },
  yes: { color: C.good, fontWeight: 700, whiteSpace: 'nowrap' },
  no: { color: C.weak },
  note: { color: C.ink2, fontSize: 13, fontStyle: 'italic', marginTop: 6 },
  lead: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 14,
    marginBottom: 10
  },
  leadInfo: { fontSize: 16, color: C.ink2, cursor: 'help' },
  bars: { display: 'flex', alignItems: 'flex-end', gap: 4, minHeight: 74 },
  barCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    width: 22,
    flex: '0 0 22px'
  },
  barTrack: {
    width: 16,
    height: 52,
    background: '#f1efeb',
    borderRadius: 3,
    display: 'flex',
    alignItems: 'flex-end',
    overflow: 'hidden'
  },
  barDay: { fontSize: 10, color: C.ink2, lineHeight: 1.1, textAlign: 'center' },
  worst: { fontSize: 13, marginTop: 8 },
  more: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: C.ink2,
    fontSize: 12,
    marginBottom: 10
  }
}))

const shortDate = (iso) => fmtDate(iso, { day: 'numeric', month: 'short' })

const todayIso = () => {
  const d = new Date()
  const pad = (x) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
export const monthName = (ym, opts = { month: 'long', year: 'numeric' }) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-IN', opts)
export const monthChoices = () => {
  const out = [todayIso().slice(0, 7)]
  while (out.length < MONTHS_BACK) out.push(prevMonth(out[out.length - 1]))
  return out
}
// One month earlier / later for the pop-up's arrows, kept inside the months
// the Period picker offers. From Last 4 weeks or custom dates, ‹ goes to last
// month and › to this month.
const stepMonth = (range, dir) => {
  const months = monthChoices()
  const i = months.indexOf(range)
  if (i === -1) return dir < 0 ? months[1] : months[0]
  return months[i - dir] || null
}

// The days a period scores, the days it is compared with, and its words.
// Every ISO day from..to (inclusive).
const daysFrom = (from, to) => {
  const out = []
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d)
  return out
}

const spanFor = (range, data, custom) => {
  if (range === CUSTOM) {
    const today = todayIso()
    const [a, b] = [custom.from, custom.to].sort()
    const now = daysFrom(a, b > today ? today : b)
    const n = now.length
    return {
      now,
      // The same number of days just before, for the comparison.
      prev: n ? daysFrom(addDays(now[0], -n), addDays(now[0], -1)) : [],
      label: n
        ? `${shortDate(now[0])} – ${shortDate(now[n - 1])} (${n} days)`
        : 'no days picked',
      vs: `vs ${n} days before`
    }
  }
  if (range === LAST4) {
    const now = data.weeks.slice(-4).flatMap(weekDays)
    return {
      now,
      prev: data.weeks.slice(-8, -4).flatMap(weekDays),
      label: `last 4 weeks, ${shortDate(now[0])} – ${shortDate(
        now[now.length - 1]
      )}`,
      vs: 'vs previous 4 wks'
    }
  }
  const today = todayIso()
  const now = monthDays(range, today)
  return {
    now,
    prev: monthDays(prevMonth(range)),
    label: `${monthName(range)}${
      today.slice(0, 7) === range ? ' (so far)' : ''
    }`,
    vs: `vs ${monthName(prevMonth(range), { month: 'short' })}`
  }
}

const INFO_TABLE = {
  borderCollapse: 'collapse',
  margin: '8px 0',
  fontSize: 13,
  width: '100%'
}
const INFO_CELL = {
  border: '1px solid rgba(255,255,255,.35)',
  padding: '5px 10px',
  textAlign: 'left',
  verticalAlign: 'top'
}
// The ⓘ box (Satya, 28 Oct): one row per section — how the score out of 5
// is worked out, a worked example, and what to do when it is red. Wide
// tooltip (classes.infoTip) so the rows don't wrap into a column of words.
const INFO_ROWS = [
  [
    'Punctuality',
    'Days on time ÷ days she worked × 5 (Keka)',
    'On time 14 of 18 days → 14 ÷ 18 × 5 = 3.9',
    'Talk about arrival time'
  ],
  [
    'Moments',
    'Daily Updates posted on time ÷ updates due × 5',
    '45 of 60 on time → 45 ÷ 60 × 5 = 3.8',
    'Set a reminder at the time updates slip'
  ],
  [
    'Environment · Facilitation',
    'Yes answers ÷ questions answered × 5, in her Classroom Observations',
    '7 Yes of 9 → 7 ÷ 9 × 5 = 3.9',
    'Observe her class this week'
  ],
  [
    'Weekly Submissions · Lesson Planner',
    'Handed in ÷ due × 5',
    '12 of 16 → 12 ÷ 16 × 5 = 3.8',
    'Get it in by Friday'
  ]
]

const INFO = (
  <div style={{ fontSize: 13.5, lineHeight: 1.45 }}>
    Every score is <b>done ÷ due × 5</b>, over the weeks shown.{' '}
    <b>Red under 3 = act this week</b> · amber 3–3.9 · green 4+.
    <table style={INFO_TABLE}>
      <thead>
        <tr>
          <th style={INFO_CELL}>Section</th>
          <th style={INFO_CELL}>Score</th>
          <th style={INFO_CELL}>Example</th>
          <th style={INFO_CELL}>If red</th>
        </tr>
      </thead>
      <tbody>
        {INFO_ROWS.map(([name, how, eg, todo]) => (
          <tr key={name}>
            <td style={{ ...INFO_CELL, fontWeight: 700 }}>{name}</td>
            <td style={INFO_CELL}>{how}</td>
            <td style={{ ...INFO_CELL, whiteSpace: 'nowrap' }}>{eg}</td>
            <td style={INFO_CELL}>{todo}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <b>Overall</b> = average of the sections that have a score; — is left out,
    not 0. Example: Punctuality 4.0 and Environment 3.0, rest — → (4.0 + 3.0) ÷
    2 = 3.5.
  </div>
)

// The ⓘ on the Actions tab (Satya, 28 Oct: "what does the tick do?").
const ACTION_INFO_LINES = [
  [
    'To do',
    'This week’s list for the CD, made on Monday from the last 4 weeks and fixed until Sunday. One line per teacher at a time.'
  ],
  [
    '☐ Tick',
    'Tick it once you have done the to-do (talked to her, observed the class, sat with her). It counts in “X of 5 done”. You can untick only in the same week.'
  ],
  ['Not ticked', 'It stays on next week’s list, marked “Open since”.'],
  [
    'After a tick',
    'Her number is left alone for 2 full weeks, then checked once. Target met = 3.0 or more (out of red). The result shows in Did it work? and History.'
  ],
  [
    'History',
    'Teachers × weeks: ✓ met · ✗ missed · … checking · ○ open · – not done. Click a cell for the details.'
  ]
]

const ACTION_INFO = (
  <div style={{ fontSize: 13.5, lineHeight: 1.45 }}>
    <table style={INFO_TABLE}>
      <tbody>
        {ACTION_INFO_LINES.map(([name, text]) => (
          <tr key={name}>
            <td style={{ ...INFO_CELL, fontWeight: 700, whiteSpace: 'nowrap' }}>
              {name}
            </td>
            <td style={INFO_CELL}>{text}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

function Score({ v, classes, why }) {
  if (v === null || v === undefined)
    return (
      <Tooltip title={why || 'No data yet'}>
        <span className={`${classes.pill} ${classes.none}`}>—</span>
      </Tooltip>
    )
  return (
    <span className={`${classes.pill} ${classes[band(v)]}`}>
      {v.toFixed(1)}
    </span>
  )
}

const blankReason = (key, t) => {
  if (key === 'pun')
    return t && t.alfred_only
      ? 'Not in Keka, so there are no clock-ins to count'
      : 'No clock-ins in Keka for these weeks'
  if (t && isLeader(t)) return 'Not scored for CD · DCD · AM yet'
  if (key === 'mom') {
    if (t && !t.linked) return 'Keka name matches no Alfred teacher account'
    if (t && isPreschool(t) && !(t.updates && t.updates.batches))
      return 'Not assigned to any preschool batch in Alfred'
    if (t && isPreschool(t))
      return 'No Daily Updates due on the days she clocked in'
    return 'No children came to her after-school or playdate sessions in these weeks'
  }
  if (t && !t.linked) return 'Keka name matches no Alfred teacher account'
  if (key === 'sub' || key === 'plan')
    return 'Nothing due: not on a preschool batch in Alfred, or no weekly theme published for these weeks'
  return 'No Classroom Observation of her in these weeks'
}

// ---- trend chart -------------------------------------------------------

function TrendChart({ series, labels, only, classes }) {
  const W = 340
  const H = 150
  const L = 22
  const R = 12
  const T = 10
  const B = 22
  const n = labels.length
  const x = (i) => (n === 1 ? (L + W - R) / 2 : L + (i * (W - L - R)) / (n - 1))
  // 0 to 5: a week scored 0 sits on the bottom line, not below the box.
  const y = (v) => T + ((5 - v) * (H - T - B)) / 5
  const every = n > 8 ? 2 : 1
  const shown = SECTIONS.filter((s) => !only || s.key === only)
  const hasAny = shown.some((s) => series.some((p) => p[s.key] !== null))
  if (!hasAny) return <div className={classes.empty}>No scores yet.</div>
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img"
      aria-label="Scores over time"
    >
      {[0, 1, 2, 3, 4, 5].map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={C.line} />
          <text
            x={L - 8}
            y={y(v) + 4}
            textAnchor="end"
            fontSize="11"
            fill={C.ink2}
          >
            {v}
          </text>
        </g>
      ))}
      {labels.map((lab, i) =>
        // The last label always shows, so a regular one too close before it
        // is skipped; the two end labels hug the edges so neither is clipped.
        (i % every === 0 && n - 1 - i >= every) || i === n - 1 ? (
          <text
            key={lab}
            x={x(i)}
            y={H - 6}
            textAnchor={
              n === 1
                ? 'middle'
                : i === 0
                ? 'start'
                : i === n - 1
                ? 'end'
                : 'middle'
            }
            fontSize="11"
            fill={C.ink2}
          >
            {lab}
          </text>
        ) : null
      )}
      {shown.map((s) => {
        // A blank week is skipped: the line runs on to the next scored week
        // instead of breaking or dropping to 0.
        const pts = []
        series.forEach((p, i) => {
          if (p[s.key] !== null) pts.push([x(i), y(p[s.key])])
        })
        return (
          <g key={s.key}>
            {pts.length > 1 ? (
              <polyline
                points={pts.map((pt) => pt.join(',')).join(' ')}
                fill="none"
                stroke={s.color}
                strokeWidth={only ? 2.6 : 2}
                strokeLinejoin="round"
              />
            ) : null}
            {series.map((p, i) =>
              p[s.key] === null ? null : (
                <circle
                  key={labels[i]}
                  cx={x(i)}
                  cy={y(p[s.key])}
                  r={only ? 3.5 : 2.6}
                  fill={s.color}
                >
                  <title>{`${s.label} · ${labels[i]}: ${p[s.key].toFixed(
                    1
                  )}`}</title>
                </circle>
              )
            )}
          </g>
        )
      })}
    </svg>
  )
}

// One section's months: its latest score and the change from the month
// before, over its own small chart.
function TrendCard({ sec, trend, classes }) {
  const vals = trend.series.map((p) => p[sec.key])
  let i = vals.length - 1
  while (i >= 0 && vals[i] === null) i -= 1
  const now = i >= 0 ? vals[i] : null
  const before = i > 0 ? vals[i - 1] : null
  const diff =
    now !== null && before !== null
      ? Math.round((now - before) * 10) / 10
      : null
  return (
    <div className={classes.miniCard}>
      <div className={classes.miniHead}>
        <span className={classes.secTitle}>
          <span className={classes.dot} style={{ background: sec.color }} />
          {sec.label}
        </span>
        <span className={classes.miniNow}>
          {diff !== null && (
            <span
              className={diff >= 0 ? classes.up : classes.down}
              title={`vs ${trend.labels[i - 1]}`}
            >
              {diff >= 0 ? '▲' : '▼'} {Math.abs(diff).toFixed(1)}
            </span>
          )}
          <Score v={now} classes={classes} />
        </span>
      </div>
      <TrendChart
        series={trend.series}
        labels={trend.labels}
        only={sec.key}
        classes={classes}
      />
    </div>
  )
}

// ---- detail dialog -----------------------------------------------------

// Every Classroom Observation behind an Environment / Facilitation score.
function ObservationList({ list, section, classes }) {
  if (!list.length) return null
  return (
    <div className={classes.scroll}>
      <table className={classes.mini}>
        <thead>
          <tr>
            <th className={classes.miniTh}>Date</th>
            <th className={classes.miniTh}>Class</th>
            <th className={classes.miniTh}>Observed by</th>
            <th className={classes.miniTh}>Yes</th>
            <th className={classes.miniTh}>Answered No</th>
          </tr>
        </thead>
        <tbody>
          {list.map((o, i) => {
            const qs = o[section]
            const no = qs.filter((q) => q.a === 'no').map((q) => q.label)
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${o.d}${i}`}>
                <td className={classes.miniTd} style={{ whiteSpace: 'nowrap' }}>
                  {fmtDate(o.d, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}
                </td>
                <td className={classes.miniTd}>
                  {o.kind} · {o.title}
                </td>
                <td className={classes.miniTd}>{o.by || '—'}</td>
                <td className={`${classes.miniTd} ${classes.yes}`}>
                  {qs.length - no.length} of {qs.length}
                </td>
                <td className={`${classes.miniTd} ${classes.no}`}>
                  {no.join(' · ') || '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Preschool Daily Updates: one bar per day she clocked in, its height the
// share posted on time, so a bad day stands out; numbers on hover, and the
// worst day called out when it is clearly worse than the rest.
const shareColor = (share) =>
  share >= 0.9 ? C.good : share >= 0.6 ? C.ok : C.weak

function UpdateBars({ mom, classes }) {
  const pct = Math.round((mom.ok / mom.req) * 100)
  const worst = mom.days.reduce(
    (w, x) => (!w || x.ok / x.req < w.ok / w.req ? x : w),
    null
  )
  const dayLabel = (d, opts) => fmtDate(d, opts)
  const how =
    'Updates due for the children in her batches, on the days she clocked ' +
    `in. Late or missing counts as a miss. ${mom.ok}/${mom.req} × 5 = ` +
    mom.score.toFixed(1)
  return (
    <>
      <div className={classes.lead}>
        <b>
          {mom.ok} of {mom.req}
        </b>
        &nbsp;Daily Updates on time · {pct}%
        <Tooltip title={how} placement="top">
          <InfoOutlinedIcon
            className={classes.leadInfo}
            aria-label="How this is worked out"
          />
        </Tooltip>
      </div>
      <div className={classes.scroll}>
        <div className={classes.bars}>
          {mom.days.map((x) => {
            const share = x.req ? x.ok / x.req : 1
            return (
              <Tooltip
                key={x.d}
                title={`${dayLabel(x.d, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })}: ${x.ok} of ${x.req} on time${
                  x.ok < x.req ? `, ${x.req - x.ok} late or missing` : ''
                }`}
                placement="top"
              >
                <div className={classes.barCol}>
                  <div className={classes.barTrack}>
                    <div
                      style={{
                        width: '100%',
                        height: `${Math.max(share * 100, 4)}%`,
                        background: shareColor(share)
                      }}
                    />
                  </div>
                  <span className={classes.barDay}>
                    {dayLabel(x.d, { weekday: 'narrow' })}
                    <br />
                    {dayLabel(x.d, { day: 'numeric' })}
                  </span>
                </div>
              </Tooltip>
            )
          })}
        </div>
      </div>
      {worst && worst.ok / worst.req < 0.6 && (
        <div className={classes.worst} style={{ color: C.weak }}>
          Worst day:{' '}
          <b>
            {dayLabel(worst.d, {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            })}
          </b>{' '}
          — {worst.req - worst.ok} of {worst.req} missed
        </div>
      )}
    </>
  )
}

// `only` = one section key (pun, mom, sub, plan): opened from an action, so
// the pop-up shows just that section (Satya, 28 Oct: Open showed all six);
// "Show all sections" brings the rest back.
function Detail({ t, data, span, range, onRange, onClose, only, classes }) {
  const [all, setAll] = useState(false)
  // A week box opens what that week counted (Satya, 28 Oct): the children
  // (Weekly Submissions) or the class plans (Lesson Planner).
  const [week, setWeek] = useState(null)
  const openWeek = (kind, label, d) => {
    setWeek({ kind, label, d, rows: null, error: false })
    getScorecardWeekDetail({ no: t.no, kind, week: d })
      .then((res) =>
        setWeek((w) =>
          w && w.d === d && w.kind === kind ? { ...w, rows: res.rows } : w
        )
      )
      .catch(() =>
        setWeek((w) =>
          w && w.d === d && w.kind === kind ? { ...w, error: true } : w
        )
      )
  }
  const shows = (key) => all || !only || only === key
  const dates = span.now
  const s = scoresForDates(t, dates)
  const mom = moments(t, dates)
  const first = dates[0]
  const last = dates[dates.length - 1]
  const env = observedBetween(t, first, last, 'env')
  const fac = observedBetween(t, first, last, 'fac')
  const head = (key) => {
    const sec = SECTIONS.find((x) => x.key === key)
    return (
      <div className={classes.secHead}>
        <span className={classes.secTitle}>
          <span className={classes.dot} style={{ background: sec.color }} />
          {sec.label}
        </span>
        <Score v={s[key]} classes={classes} why={blankReason(key, t)} />
      </div>
    )
  }
  return (
    <Dialog open onClose={onClose} fullScreen>
      <DialogContent>
        <div className={classes.dHead}>
          <div>
            <Typography variant="h6">{t.name}</Typography>
            <div className={classes.sub}>
              {t.alfred_only ? '' : `${t.no} · `}
              {centreOf(t)} · {t.job}
            </div>
            {/* Change the month without closing her pop-up (23 Oct): it
                moves the page's Period too, so both stay on the same days. */}
            <div className={classes.monthStep}>
              <IconButton
                size="small"
                aria-label="Earlier month"
                disabled={!stepMonth(range, -1)}
                onClick={() => onRange(stepMonth(range, -1))}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <TextField
                select
                size="small"
                variant="outlined"
                value={range}
                onChange={(e) => onRange(e.target.value)}
                inputProps={{ 'aria-label': 'Period' }}
              >
                <MenuItem value={LAST4}>Last 4 weeks</MenuItem>
                {monthChoices().map((m) => (
                  <MenuItem key={m} value={m}>
                    {monthName(m)}
                  </MenuItem>
                ))}
                {range === CUSTOM && (
                  <MenuItem value={CUSTOM}>{span.label}</MenuItem>
                )}
              </TextField>
              <IconButton
                size="small"
                aria-label="Later month"
                disabled={!stepMonth(range, 1)}
                onClick={() => onRange(stepMonth(range, 1))}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className={classes.sub}>Overall</span>
            <Score v={s.overall} classes={classes} />
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {only && !all && (
          <Button size="small" color="primary" onClick={() => setAll(true)}>
            Show all sections
          </Button>
        )}

        {shows('pun') && (
          <div className={classes.sec}>
            {head('pun')}
            {t.alfred_only ? (
              <div className={classes.note}>
                {t.name} has classes in Alfred but no Keka record, so there are
                no clock-ins to count.
              </div>
            ) : (
              <PunctualitySection
                t={t}
                dates={dates}
                kekaLatest={data.keka_latest}
              />
            )}
          </div>
        )}

        {shows('mom') && (
          <div className={classes.sec}>
            {head('mom')}
            {mom.preschool && t.linked ? (
              <>
                {!t.updates || !t.updates.batches ? (
                  <div className={classes.note}>
                    Not assigned to any preschool batch in Alfred, so no updates
                    can be counted for her.
                  </div>
                ) : mom.req ? (
                  <UpdateBars mom={mom} classes={classes} />
                ) : (
                  <div className={classes.note}>
                    No Daily Updates due on the days she clocked in.
                  </div>
                )}
              </>
            ) : t.linked ? (
              <>
                <div className={classes.calc}>
                  Moments app · {mom.done} of {mom.due} children got{' '}
                  {MOMENTS_A_MONTH}+ moments a month in her classes
                  {mom.score !== null
                    ? ` → ${mom.done}/${mom.due} × 5 = ${mom.score.toFixed(1)}`
                    : ''}
                </div>
                <div className={classes.note}>
                  One count per child, per class, per month they came to a
                  session she taught. Moments from any of the class&apos;s
                  teachers count. A month with under 14 days in the period needs
                  1. At Apartment sessions can&apos;t carry moments yet, so they
                  are left out.
                </div>
                {mom.short.length ? (
                  <div style={{ fontSize: 13 }}>
                    <b>Short:</b> {mom.short.length} child-month
                    {mom.short.length === 1 ? '' : 's'} (
                    {mom.short.filter((x) => x.got === 0).length} with no
                    moment)
                  </div>
                ) : mom.due ? (
                  <div className={classes.note}>
                    Every child got enough moments.
                  </div>
                ) : null}
              </>
            ) : (
              <div className={classes.calc}>
                “{t.name}” in Keka matches no single Alfred teacher account by
                name, so moments can&apos;t be counted yet.
              </div>
            )}
          </div>
        )}

        {[
          ['env', env],
          ['fac', fac]
        ]
          .filter(([key]) => shows(key))
          .map(([key, o]) => (
            <div key={key} className={classes.sec}>
              {head(key)}
              <div className={classes.calc}>
                Centre Ops › Classroom Observations
                {o.score !== null
                  ? ` · ${o.list.length} observation${
                      o.list.length === 1 ? '' : 's'
                    }, ${o.yes} of ${o.n} answered Yes → ${o.yes}/${
                      o.n
                    } × 5 = ${o.score.toFixed(1)}`
                  : t.linked
                  ? ' · none for her in these weeks'
                  : ' · her Keka name matches no Alfred teacher account, so her observations can’t be found'}
              </div>
              <ObservationList list={o.list} section={key} classes={classes} />
            </div>
          ))}

        {[
          [
            'sub',
            'evals',
            'Programs › Preschool › Weekly Submissions',
            'evaluations submitted'
          ],
          ['plan', 'plans', 'Teacher › Lesson Planner', 'plans submitted']
        ]
          .filter(([key]) => shows(key))
          .map(([key, kind, where, what]) => {
            const w = weeklyWork(t, dates, kind)
            return (
              <div key={key} className={classes.sec}>
                {head(key)}
                <div className={classes.calc}>
                  {where}
                  {w.score !== null
                    ? ` · ${w.done} of ${w.due} ${what} → ${w.done}/${
                        w.due
                      } × 5 = ${w.score.toFixed(
                        1
                      )} · shared by the class's teachers; only weeks with a published weekly theme are due`
                    : ` · ${blankReason(key, t).toLowerCase()}`}
                </div>
                {w.weeks.length > 0 && (
                  <div className={classes.weekRow}>
                    {w.weeks.map((x) => (
                      <button
                        type="button"
                        key={x.d}
                        className={classes.weekBox}
                        style={
                          x.done < x.due
                            ? { background: C.weakBg, color: C.weak }
                            : { background: C.goodBg, color: C.good }
                        }
                        title="Click to see the list for this week"
                        onClick={() => openWeek(kind, where, x.d)}
                      >
                        {fmtDate(x.d, { day: 'numeric', month: 'short' })} ·{' '}
                        {x.done}/{x.due}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
      </DialogContent>
      {week && (
        <Dialog open onClose={() => setWeek(null)} maxWidth="sm" fullWidth>
          <DialogContent>
            <div className={classes.dHead}>
              <div>
                <Typography variant="h6">
                  {week.kind === 'evals'
                    ? 'Weekly Submissions'
                    : 'Lesson Planner'}{' '}
                  · week of{' '}
                  {fmtDate(week.d, { day: 'numeric', month: 'short' })}
                </Typography>
                <div className={classes.sub}>
                  {t.name} · {week.label}
                </div>
              </div>
              <IconButton onClick={() => setWeek(null)} aria-label="Close week">
                <CloseIcon />
              </IconButton>
            </div>
            {week.error && (
              <Typography color="error">Could not load this week.</Typography>
            )}
            {!week.error && !week.rows && <CircularProgress size={24} />}
            {week.rows && !week.rows.length && (
              <div className={classes.sub}>Nothing was due this week.</div>
            )}
            {week.rows && week.rows.length > 0 && (
              <table className={classes.weekTable}>
                <thead>
                  <tr>
                    <th>{week.kind === 'evals' ? 'Child' : 'Class plan'}</th>
                    <th>Level</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {week.rows.map((r, i) => (
                    <tr key={`${r.name}-${i}`}>
                      <td>{r.name || '—'}</td>
                      <td>{r.class}</td>
                      <td style={{ color: r.done ? C.good : C.weak }}>
                        {r.done ? '✓ ' : '✗ '}
                        {r.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  )
}

// ---- page --------------------------------------------------------------

// A score column's heading: click to sort highest first, again for lowest
// first, a third time to go back to the normal order.
function SortHead({ col, label, sort, onSort, classes }) {
  const on = sort.k === col
  return (
    <button
      type="button"
      className={classes.sortBtn}
      aria-label={`Sort by ${label}`}
      onClick={() => {
        if (!on) onSort({ k: col, d: 'desc' })
        else if (sort.d === 'desc') onSort({ k: col, d: 'asc' })
        else onSort({ k: null, d: null })
      }}
    >
      {label}
      <span className={`${classes.arrow} ${on ? classes.arrowOn : ''}`}>
        {on ? (sort.d === 'desc' ? '▼' : '▲') : '▲▼'}
      </span>
    </button>
  )
}

// Inside Preschool Quality & Ops (28 Oct) the centre comes from the picker
// above both views (centreName, Alfred's name) and the card on top shows
// the centre's last-4-weeks average (onSummary).
// Inside Preschool Quality & Ops (Satya, 28 Oct) the Period is the page's
// one picker beside the Centre: range / custom / onRange come from there,
// and the card's headline follows it.
export default function TeacherScorecard({
  centreName,
  onSummary,
  range: sharedRange,
  custom: sharedCustom,
  onRange,
  view,
  headSlot
} = {}) {
  const classes = useStyles()
  const calendar = useCalendarDaysOff()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [attempt, setAttempt] = useState(0)
  const [tab, setTab] = useState(view === 'actions' ? 'actions' : 'trend')
  // Inside Quality and Ops, Actions is its own top tab (Satya, 28 Oct: three
  // tabs beside Period and Centre), so `view` picks it from outside.
  useEffect(() => {
    if (view === 'actions') setTab('actions')
    else if (view === 'scorecard')
      setTab((t) => (t === 'actions' ? 'trend' : t))
  }, [view])
  const [centre, setCentre] = useState('')
  // Preschool teachers + centre leaders by default; afterschool only on
  // "Everyone"
  // (22 Oct: "show preschool only? randomly Afterschool is coming").
  // Preschool teachers only for now (Satya, 28 Oct: Teachers picker removed).
  // Preschool | After-school back as a picker (Satya, 4 Nov: "show in
  // local, add all possible"); All (preschool + after-school, no CD·DCD·AM)
  // is the default (4 Nov); the headline card + Actions stay preschool.
  const [group, setGroup] = useState('teachers')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState({ k: null, d: null })
  const [openNo, setOpenNo] = useState(null)
  // The section an action's Open asked for (null = all of them).
  const [openOnly, setOpenOnly] = useState(null)
  const openTeacher = (no, only = null) => {
    setOpenOnly(only)
    setOpenNo(no)
  }
  const [who, setWho] = useState(ALL)
  // Month by month (Shruti, 22 Oct: x axis = month).
  const period = 'month'
  const [ownRange, setOwnRange] = useState(LAST4)
  const [ownCustom, setCustom] = useState(() => ({
    from: addDays(todayIso(), -27),
    to: todayIso()
  }))
  const sharedPeriod = sharedRange !== undefined
  const range = sharedPeriod ? sharedRange : ownRange
  const custom = sharedPeriod ? sharedCustom : ownCustom
  const setRange = sharedPeriod ? onRange : setOwnRange

  // Every teacher in one answer: the server reads the weeks it stored
  // (Satya, 23 Oct), so nothing is worked out while the page waits. If some
  // older weeks are not stored yet it asks again shortly, keeping what it
  // shows meanwhile.
  useEffect(() => {
    let cancelled = false
    let retry = null
    if (!attempt) setError(null)
    getTeacherScorecard({ weeks: WEEKS })
      .then((d) => {
        if (cancelled) return
        // Alfred-only teachers carry Alfred's centre name; the pickers use
        // Keka's spelling ("HRBR" vs "HRBR Layout").
        const keka = [
          ...new Set(d.teachers.filter((t) => !t.alfred_only).map(centreOf))
        ]
        d.teachers = d.teachers.map((t) =>
          t.alfred_only ? { ...t, cc: kekaCentreFor(t.cc, keka) || t.cc } : t
        )
        setData(d)
        if (d.missing && d.missing.length)
          retry = setTimeout(() => setAttempt((n) => n + 1), FILL_RETRY_MS)
      })
      .catch((err) => {
        if (cancelled || attempt) return
        const detail =
          err && err.response && err.response.data && err.response.data.detail
        setError(detail || 'Could not load the scorecard. Please refresh.')
      })
    return () => {
      cancelled = true
      clearTimeout(retry)
    }
  }, [attempt])

  const ready = Boolean(data)
  const missing = (data && data.missing) || []

  const centres = useMemo(
    () => (data ? [...new Set(data.teachers.map(centreOf))].sort() : []),
    [data]
  )

  // A shared centre Keka doesn't know leaves the page's own Centre picker
  // showing, so the user can still pick by hand.
  const shared = centreName !== undefined
  const sharedMatch = useMemo(
    () => (shared ? kekaCentreFor(centreName, centres) : null),
    [shared, centreName, centres]
  )
  useEffect(() => {
    if (shared && data) setCentre(sharedMatch || '')
  }, [shared, sharedMatch, data])

  // The centre and teacher pickers narrow both tabs; the header filters only
  // the table.
  const inCentre = useMemo(
    () =>
      data
        ? data.teachers.filter(
            (t) =>
              (!centre || centreOf(t) === centre) &&
              (group === 'all' ||
                (group === 'leaders' && isLeader(t)) ||
                (group === 'preschool' && isPreschool(t) && !isLeader(t)) ||
                (group === 'afterschool' && !isPreschool(t) && !isLeader(t)) ||
                (group === 'teachers' && !isLeader(t)) ||
                (group === 'preschoolLeaders' &&
                  (isPreschool(t) || isLeader(t))))
          )
        : [],
    [data, centre, group]
  )
  // The headline card and Actions: preschool, whatever the picker says.
  const preschoolInCentre = useMemo(
    () =>
      data
        ? data.teachers.filter(
            (t) =>
              (!centre || centreOf(t) === centre) &&
              isPreschool(t) &&
              !isLeader(t)
          )
        : [],
    [data, centre]
  )
  // Weekly Submissions + Lesson Planner are preschool work only.
  const shownSections =
    group === 'afterschool'
      ? SECTIONS.filter((s) => s.key !== 'sub' && s.key !== 'plan')
      : SECTIONS

  const span = useMemo(
    () => (data ? spanFor(range, data, custom) : null),
    [data, range, custom]
  )

  // The card's headline: last 4 weeks, whatever the Period picker says, for
  // the teachers the centre and Teachers pickers leave.
  const summary = useMemo(() => {
    if (!data || calendar === 'loading') return null
    const dates = spanFor(sharedPeriod ? range : LAST4, data, custom).now
    const scores = preschoolInCentre.map((t) => scoresForDates(t, dates))
    return {
      overall: averageScores(scores).overall,
      teachers: scores.filter((x) => x.overall !== null).length,
      weak: scores.filter((x) => x.overall !== null && x.overall < 3).length
    }
  }, [data, preschoolInCentre, calendar, custom, range, sharedPeriod])
  useEffect(() => {
    if (onSummary) onSummary(error ? { error } : summary)
  }, [summary, error])

  const rows = useMemo(() => {
    if (!data || calendar === 'loading') return []
    const q = query.trim().toLowerCase()
    const list = inCentre
      .map((t) => {
        const now = scoresForDates(t, span.now)
        const before = scoresForDates(t, span.prev)
        const diff =
          now.overall !== null && before.overall !== null
            ? Math.round((now.overall - before.overall) * 10) / 10
            : null
        // Classroom Observations of her in the period (Environment and
        // Facilitation come from the same visits).
        const first = span.now[0]
        const last = span.now[span.now.length - 1]
        const seen = (t.observations || []).filter(
          (o) => o.d >= first && o.d <= last
        ).length
        // Every column's change vs the period before (Satya, 28 Oct), not
        // only Overall.
        const change = Object.fromEntries(
          SECTIONS.map((s) => [
            s.key,
            now[s.key] !== null && before[s.key] !== null
              ? Math.round((now[s.key] - before[s.key]) * 10) / 10
              : null
          ])
        )
        return { t, now: { ...now, seen }, before, diff, change }
      })
      .filter(
        ({ t }) =>
          !q ||
          `${t.name} ${t.no} ${centreOf(t)} ${t.job}`.toLowerCase().includes(q)
      )
    if (!sort.k) return list
    // Blank scores always sink to the bottom, whichever way it sorts.
    const val = (r) => (sort.k === 'diff' ? r.diff : r.now[sort.k])
    return [...list].sort((a, b) => {
      const x = val(a)
      const y = val(b)
      if (x === null) return y === null ? 0 : 1
      if (y === null) return -1
      return sort.d === 'desc' ? y - x : x - y
    })
  }, [data, inCentre, query, sort, span, calendar])

  const trend = useMemo(() => {
    if (!data || calendar === 'loading') return { labels: [], series: [] }
    const bs = buckets(data.weeks, period)
    const pick =
      who === ALL ? inCentre : inCentre.filter((t) => t.no === who).slice(0, 1)
    const series = bs.map((b) =>
      averageScores(pick.map((t) => scoresFor(t, b.mondays)))
    )
    // Months before the first score (Keka and Alfred data start in June)
    // are left off rather than drawn as an empty stretch.
    const first = series.findIndex((p) => p.overall !== null)
    const from = first === -1 ? 0 : first
    return {
      labels: bs.slice(from).map((b) => b.label),
      series: series.slice(from)
    }
  }, [data, inCentre, who, period, calendar])

  // A teacher picked for the trend who is outside the new centre falls back
  // to the average, so the picker never shows a value it doesn't list.
  useEffect(() => {
    if (who !== ALL && !inCentre.some((t) => t.no === who)) setWho(ALL)
  }, [inCentre, who])

  // Inside Preschool Quality & Ops the page's own Period picker is used
  // instead (sharedPeriod).
  const periodControls = !sharedPeriod && (
    <>
      {tab === 'teachers' && (
        <TextField
          select
          size="small"
          variant="outlined"
          label="Period"
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className={classes.select}
        >
          <MenuItem value={LAST4}>Last 4 weeks</MenuItem>
          {monthChoices().map((m) => (
            <MenuItem key={m} value={m}>
              {monthName(m)}
            </MenuItem>
          ))}
          <MenuItem value={CUSTOM}>Custom dates…</MenuItem>
        </TextField>
      )}
      {tab === 'teachers' &&
        range === CUSTOM &&
        [
          ['from', 'From'],
          ['to', 'To']
        ].map(([k, label]) => (
          <TextField
            key={k}
            type="date"
            size="small"
            variant="outlined"
            label={label}
            value={custom[k]}
            onChange={(e) =>
              e.target.value &&
              setCustom((c) => ({ ...c, [k]: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: addDays(todayIso(), -MAX_BACK_DAYS),
              max: todayIso()
            }}
            className={classes.select}
          />
        ))}
    </>
  )

  const info = (
    <Tooltip
      title={tab === 'actions' ? ACTION_INFO : INFO}
      placement="bottom-start"
      interactive
      classes={{ tooltip: classes.infoTip }}
    >
      <InfoOutlinedIcon className={classes.info} />
    </Tooltip>
  )

  const open = data && openNo && data.teachers.find((t) => t.no === openNo)
  const sortProps = { sort, onSort: setSort, classes }

  return (
    <div className={shared ? undefined : classes.root}>
      {!shared && (
        <div className={classes.title}>
          <Typography variant="h5">Teacher Scorecard</Typography>
          {info}
        </div>
      )}

      {!(shared && sharedMatch && !periodControls) && (
        <div className={classes.bar}>
          {!(shared && sharedMatch) && (
            <TextField
              select
              size="small"
              variant="outlined"
              label="Centre"
              disabled={!ready}
              value={centre}
              onChange={(e) => setCentre(e.target.value)}
              className={classes.select}
            >
              <MenuItem value="">All centres</MenuItem>
              {centres.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          )}
          {periodControls}
        </div>
      )}

      {/* ⓘ beside Period when the page gives a slot (Satya, 28 Oct). */}
      {shared && headSlot && createPortal(info, headSlot)}
      {view === 'actions' ? (
        // Actions top tab: no sub-tabs; its ⓘ is in the header row.
        shared && !headSlot && <div className={classes.title}>{info}</div>
      ) : (
        <div className={classes.subTabs}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
          >
            <Tab value="trend" label="Trend" />
            <Tab value="teachers" label="Teachers" />
            {!view && <Tab value="actions" label="Actions" />}
          </Tabs>
          {tab !== 'actions' && (
            // Two-button switch, not a dropdown (Satya, 4 Nov: "can the
            // button improve?") — both choices in sight, tab height.
            <ToggleButtonGroup
              exclusive
              size="small"
              value={group}
              onChange={(e, v) => {
                if (!v) return
                setGroup(v)
                setWho(ALL)
              }}
              className={classes.groupSwitch}
              aria-label="Teachers"
            >
              <ToggleButton value="teachers" disabled={!ready}>
                All
              </ToggleButton>
              <ToggleButton value="preschool" disabled={!ready}>
                Preschool
              </ToggleButton>
              <ToggleButton value="afterschool" disabled={!ready}>
                After-school
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {shared && !headSlot && info}
        </div>
      )}

      {error && <div className={classes.empty}>{error}</div>}
      {!error && !data && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}

      {missing.length > 0 && (
        <div className={classes.more}>
          <CircularProgress size={14} />
          {WEEKS - missing.length} of {WEEKS} weeks ready · the older weeks are
          still being filled in and appear here by themselves.
        </div>
      )}

      {ready && tab === 'trend' && (
        <div className={classes.card}>
          <div className={classes.cardHead}>
            <div className={classes.bar}>
              {/* Type a name or centre to find her (23 Oct: the list ran to
                  96 names); cleared = the average again. */}
              <Autocomplete
                size="small"
                options={inCentre}
                value={inCentre.find((t) => t.no === who) || null}
                onChange={(e, t) => setWho(t ? t.no : ALL)}
                getOptionLabel={(t) => `${t.name} · ${centreOf(t)}`}
                getOptionSelected={(t, v) => t.no === v.no}
                className={classes.search}
                style={{ width: 360 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Teacher"
                    placeholder={`Average of ${inCentre.length} teachers${
                      centre ? ` · ${centre}` : ''
                    } · type a name`}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true
                    }}
                  />
                )}
              />
              <span className={classes.sub}>
                Month by month · the latest month is so far
              </span>
            </div>
          </div>
          <div className={classes.cards}>
            {shownSections.map((sec) => (
              <TrendCard
                key={sec.key}
                sec={sec}
                trend={trend}
                classes={classes}
              />
            ))}
          </div>
        </div>
      )}

      {ready && tab === 'teachers' && (
        <>
          <div className={classes.tableHint}>
            {rows.length} of {inCentre.length} teachers · {span.label} · click a
            teacher for the detail · click a column name to sort
          </div>
          {/* No overflow wrapper: the page scrolls, so the header row sticks
              to the top of the window. */}
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={`${classes.th} ${classes.thWho}`}>
                  {/* The column name lives inside the box, so the header
                      stays one row tall. */}
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Teacher (search name, centre, role)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={classes.headSearch}
                    inputProps={{ 'aria-label': 'Search teachers' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="disabled" />
                        </InputAdornment>
                      )
                    }}
                  />
                </th>
                {shownSections.map((s) => (
                  <th key={s.key} className={classes.th}>
                    <SortHead col={s.key} label={s.label} {...sortProps} />
                  </th>
                ))}
                <th className={classes.th}>
                  <SortHead
                    col="seen"
                    label="Classes observed"
                    {...sortProps}
                  />
                </th>
                <th className={classes.th}>
                  <SortHead col="overall" label="Overall" {...sortProps} />
                </th>
                <th className={classes.th}>
                  <SortHead col="diff" label={span.vs} {...sortProps} />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ t, now, before, diff, change }) => (
                <tr
                  key={t.no}
                  className={classes.row}
                  tabIndex={0}
                  onClick={() => openTeacher(t.no)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openTeacher(t.no)
                    }
                  }}
                >
                  <td className={classes.td}>
                    <div className={classes.name}>{t.name}</div>
                    <div className={classes.sub}>
                      {t.alfred_only ? '' : `${t.no} · `}
                      {centreOf(t)} · {t.job}
                    </div>
                  </td>
                  {shownSections.map((s) => (
                    <td
                      key={s.key}
                      className={`${classes.td} ${classes.tdNum}`}
                    >
                      {now[s.key] === null ? (
                        <Tooltip title={blankReason(s.key, t)}>
                          <span className={classes.none}>—</span>
                        </Tooltip>
                      ) : (
                        now[s.key].toFixed(1)
                      )}
                      {/* Arrow only, beside the number; the change shows on hover
                          (Satya, 28 Oct). */}
                      {change[s.key] !== null && (
                        // Hover: the previous period's own number (Satya,
                        // 28 Oct), e.g. "Previous period: 2.9 (up 0.7)".
                        <Tooltip
                          title={`Previous period: ${before[s.key].toFixed(
                            1
                          )} (${change[s.key] >= 0 ? 'up' : 'down'} ${Math.abs(
                            change[s.key]
                          ).toFixed(1)})`}
                        >
                          <span
                            className={
                              change[s.key] >= 0 ? classes.up : classes.down
                            }
                            style={{
                              fontSize: 11.5,
                              cursor: 'help',
                              marginLeft: 4
                            }}
                          >
                            {change[s.key] >= 0 ? '▲' : '▼'}
                          </span>
                        </Tooltip>
                      )}
                    </td>
                  ))}
                  <td className={`${classes.td} ${classes.tdNum}`}>
                    {now.seen || <span className={classes.none}>0</span>}
                  </td>
                  <td className={`${classes.td} ${classes.tdNum}`}>
                    <Score v={now.overall} classes={classes} />
                  </td>
                  <td className={`${classes.td} ${classes.tdNum}`}>
                    {diff === null ? (
                      <span className={classes.none}>—</span>
                    ) : (
                      <span className={diff >= 0 ? classes.up : classes.down}>
                        {diff >= 0 ? '▲' : '▼'} {Math.abs(diff).toFixed(1)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <div className={classes.empty}>
              No teachers match these filters.
            </div>
          )}
        </>
      )}

      {ready && tab === 'actions' && calendar !== 'loading' && (
        <ActionsTab
          teachers={preschoolInCentre}
          allTeachers={data.teachers}
          weeks={data.weeks}
          complete={!missing.length}
          centre={centre}
          today={todayIso()}
          onOpen={openTeacher}
          headSlot={headSlot}
        />
      )}

      {open && (
        <Detail
          t={open}
          data={data}
          span={span}
          range={range}
          onRange={setRange}
          onClose={() => setOpenNo(null)}
          only={openOnly}
          classes={classes}
        />
      )}
    </div>
  )
}
